import { useEffect, useState } from 'react';
import { InputObj, PageItem, InputValues, FunctionInputs } from '../Interfaces/TestCaseInterface';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import ButtonGroup from '@mui/material/ButtonGroup';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteIcon from '@mui/icons-material/Delete';
import cloneDeep from 'lodash/cloneDeep'
import './InputEditor.css';

export interface InputEditorProps {
  input: InputObj,
  setInput: (iid: string, newInput: InputObj) => void,
  updateInputIx: (iid: string, adjustment: number) => void,
  deleteInput: (iid: string) => void,
  policyVariables: Record<string, PageItem>,
  pagePolicyInputs?: Array<string>,
  lastItem: boolean,
  inputError: boolean,
  editable: boolean
}

export const InputEditor = (props: InputEditorProps) => {
  const [functionCustom, setFunctionCustom] = useState(false);
  const [policyEnumeration, setPolicyEnumeration] = useState<Array<[string, string]> | undefined>();

  useEffect(() => {
    setPolicyEnumeration(undefined);
    if (props.input.policyInput === "policy" && props.pagePolicyInputs !== undefined) {
      let policyObject = props.pagePolicyInputs!.find((policyVar) => policyVar === props.input.id);
      if (policyObject === undefined)
        updatePolicyInputType(props.pagePolicyInputs![0]);
      else
        updatePolicyInputType(policyObject);
    } else if (props.input.policyInput === "function") {
      if (Object.keys(FunctionInputs).includes(props.input.inputType))
        updateFunctionInputType(props.input.inputType);
      else
        updateFunctionInputType(Object.values(FunctionInputs)[0]);
    } else if (props.input.policyInput === "custom") {
      if (Object.keys(InputValues).includes(props.input.inputType))
        updateInputType(props.input.inputType);
      else
        updateInputType(Object.keys(InputValues)[0]);
    }
  }, [props.input.policyInput])

  useEffect(() => {
    if(!policyEnumeration && props.policyVariables !== undefined && props.policyVariables[props.input.id]?.restriction !== undefined && props.policyVariables[props.input.id].restriction!.length > 0){
      setPolicyEnumeration(props.policyVariables[props.input.id].restriction);
    }
    if (props.input.policyInput === "policy" && !props.pagePolicyInputs?.includes(props.input.id)) {
      props.setInput(props.input.iid, { ...props.input, policyInput: 'custom' });
    }
    else if (props.input.policyInput === "custom" && props.pagePolicyInputs?.includes(props.input.id)) {
      props.setInput(props.input.iid, { ...props.input, policyInput: 'policy' });
    }
  }, [props.pagePolicyInputs])

  const updateId = (newId: string) => {
    if (props.input.policyInput === "function" && !functionCustom && newId === "custom-function-value") {
      setFunctionCustom(true);
      return;
    }
    else {
      setFunctionCustom(false);
    }
    let newInputObj = cloneDeep(props.input); // clone input from props
    newInputObj.id = newId;
    props.setInput(props.input.iid, newInputObj);
  } // updateId()

  const updateIdFunctionCustom = (newId: string) => {
    if(props.input.id === newId)
      return;
    let newInputObj = cloneDeep(props.input); // clone input from props
    newInputObj.id = newId;
    props.setInput(props.input.iid, newInputObj);
  } // updateId()

  const updateFunctionInputType = (newInputType: string) => {
    if(props.input.inputType as string === newInputType)
      return;
    let newInputObj: InputObj = cloneDeep(props.input); // clone input from props
    newInputObj.inputType = newInputType as FunctionInputs;
    if (JSON.stringify(newInputObj) !== JSON.stringify(props.input))
      props.setInput(props.input.iid, newInputObj);
  } // updateFunctionInputType()

  const updateInputType = (newInputType: string) => {
    if(props.input.inputType as string === newInputType)
      return;
    let newInputObj: InputObj = cloneDeep(props.input); // clone input from props
    newInputObj.inputType = newInputType as InputValues;
    if (JSON.stringify(newInputObj) !== JSON.stringify(props.input)){
      props.setInput(props.input.iid, newInputObj);
    }
  } // updateInputType()

  const updatePolicyInputType = (newPolicyInputId: string) => {
    if(!policyEnumeration && props.policyVariables[newPolicyInputId].restriction !== undefined && props.policyVariables[newPolicyInputId].restriction!.length > 0){
      setPolicyEnumeration(props.policyVariables[newPolicyInputId].restriction);
    }
    if(props.input.id === newPolicyInputId){
    return;
  }
    let newInputObj: InputObj = cloneDeep(props.input); // clone input from props
    if (newPolicyInputId.includes('Fn::')) {
      newInputObj.policyInput = "function";
      if(props.input.inputType !== FunctionInputs.OTPEmail)
      return;
      // We only expect otpEmail from policy parsing for now
      newInputObj.inputType = FunctionInputs.OTPEmail;
      var otpEmailValues = props.policyVariables[newPolicyInputId].id.split(',');
      if (otpEmailValues.length > 1) {
        newInputObj.id = otpEmailValues[0];
        newInputObj.value = otpEmailValues[1];
      }
      else {
        newInputObj.id = props.policyVariables[newPolicyInputId].id;
      }
    }
    else {
      newInputObj.inputType = props.policyVariables[newPolicyInputId].input;
      newInputObj.id = props.policyVariables[newPolicyInputId].id;
    }
    if (props.policyVariables[newPolicyInputId].restriction !== undefined && props.policyVariables[newPolicyInputId].restriction!.length > 0) {
      setPolicyEnumeration(props.policyVariables[newPolicyInputId].restriction);
      newInputObj.value = props.policyVariables[newPolicyInputId].restriction![0][0];
    } else {
      setPolicyEnumeration(undefined);
    }
    if (JSON.stringify(newInputObj) !== JSON.stringify(props.input)){
      props.setInput(props.input.iid, newInputObj);
    }
  } // updatePolicyInputType()

  const updateValue = (newValue: string) => {
    if(props.input.value === newValue)
      return;
    let newInputObj = cloneDeep(props.input); // clone input from props
    newInputObj.value = newValue;
    props.setInput(props.input.iid, newInputObj);
  } // updateValue()

  const variableSourceChange = (e: any) => {
    if(props.input.policyInput === e.target.value)
      return;
    let newInputObj: InputObj = cloneDeep(props.input); // clone input from props
    newInputObj.policyInput = e.target.value;
    if(e.target.value === 'custom' && !functionCustom){
      newInputObj.id = '';
    }
    props.setInput(props.input.iid, newInputObj);
  } // variableSourceChange()

  return (
    <TableRow>
      <TableCell>
        <TextField
          hiddenLabel
          select
          value={props.input.policyInput}
          disabled={!props.editable}
          size='small'
          variant="filled"
          fullWidth
          onChange={e => variableSourceChange(e)}
        >
          <MenuItem value="policy" disabled={props.pagePolicyInputs === undefined || props.pagePolicyInputs.length === 0}>Policy</MenuItem>
          <MenuItem value="function">Function</MenuItem>
          <MenuItem value="custom">Custom</MenuItem>
        </TextField>
      </TableCell>
      <TableCell>
        {props.input.policyInput === "function" && props.pagePolicyInputs !== undefined && props.pagePolicyInputs.length > 0 ?
          <TextField
            hiddenLabel
            select
            value={!functionCustom ? props.input.id : "custom-function-value"}
            disabled={!props.editable}
            size='small'
            variant="filled"
            fullWidth
            onChange={(e) => updateId(e.target.value)}>
            {props.pagePolicyInputs?.includes((!functionCustom ? props.input.id : "custom-function-value")) ? props.pagePolicyInputs.map(input => <MenuItem key={input} value={input}>{input}</MenuItem>) : <MenuItem value={props.input.id}>{props.input.id}</MenuItem>}
            <MenuItem key={"custom-function-value"} value={"custom-function-value"}>{"Other Id"}</MenuItem>
          </TextField>
          : <TextField
            hiddenLabel
            value={props.input.id}
            className={props.input.id === "" && props.inputError ? "emptyId" : ""}
            size='small'
            variant="filled"
            fullWidth
            disabled={!props.editable || props.input.policyInput === "policy"}
            onChange={(e) => updateId(e.target.value)}
          />}
        {props.input.policyInput === "function" && functionCustom && (props.pagePolicyInputs !== undefined && props.pagePolicyInputs.length > 0) ?
          <TextField
            hiddenLabel
            className={props.input.id === "" && props.inputError ? "emptyId" : ""}
            size='small'
            variant="filled"
            fullWidth
            value={props.input.id}
            disabled={!props.editable}
            onChange={(e) => updateIdFunctionCustom(e.target.value)}
          />
          : <></>
        }
      </TableCell>
      <TableCell>

        {props.input.policyInput === "policy" ?
          <TextField
            select
            hiddenLabel
            value={props.input.id}
            disabled={!props.editable}
            size='small'
            variant="filled"
            fullWidth
            onChange={(e) => updatePolicyInputType(e.target.value)}>
            {props.pagePolicyInputs?.includes(props.input.id) ? props.pagePolicyInputs?.map(input => <MenuItem key={input} value={input}>{input}</MenuItem>): <MenuItem key={props.input.id} value={props.input.id}>{props.input.id}</MenuItem>}
          </TextField> : <></>
        }
        {
          props.input.policyInput === "function" ?
            <TextField
              select
              hiddenLabel
              value={props.input.inputType}
              disabled={!props.editable}
              size='small'
              variant="filled"
              fullWidth
              onChange={(e) => updateFunctionInputType(e.target.value)}>
              {Object.entries(FunctionInputs).map(input => <MenuItem key={input[0]} value={input[1]}>{input[0]}</MenuItem>)}
            </TextField> : <></>
        }
        {
          props.input.policyInput === "custom" ?
            <TextField
              select
              hiddenLabel
              value={props.input.inputType as string}
              disabled={!props.editable}
              size='small'
              variant="filled"
              fullWidth
              onChange={(e) => updateInputType(e.target.value)}>
              {Object.keys(InputValues).map(input => {
                if (input !== InputValues.Null && input !== InputValues.Navigate)
                  return (<MenuItem key={input} value={input}>{input}</MenuItem>);
                else if (input === InputValues.Navigate && props.lastItem)
                  return (<MenuItem key={input} value={input}>{input}</MenuItem>);
                  else 
                  return null;
              })}
            </TextField> : <></>
        }
      </TableCell>
      <TableCell>

        {policyEnumeration ?
          <TextField
            select
            hiddenLabel
            value={props.input.value}
            disabled={!props.editable}
            size='small'
            variant="filled"
            fullWidth
            onChange={(e) => updateValue(e.target.value)}>
            {policyEnumeration !== undefined ? policyEnumeration.map(([val, label]) => <MenuItem key={val} value={val}>{label}</MenuItem>) : <MenuItem value={props.input.value}>{props.input.value}</MenuItem>}
          </TextField>
          : <TextField
            hiddenLabel
            value={props.input.value}
            disabled={!props.editable}
            size='small'
            variant="filled"
            fullWidth
            onChange={(e) => updateValue(e.target.value)}
          />}
      </TableCell>
      <TableCell>
        <ButtonGroup size='small'>
          <IconButton disabled={!props.editable} className='orderingButton' color='secondary' size="small" onClick={(e) => props.updateInputIx(props.input.iid, -1)}><ArrowUpwardIcon /></IconButton >
          <IconButton disabled={!props.editable} className='orderingButton' color='secondary' size="small" onClick={(e) => props.updateInputIx(props.input.iid, 1)}><ArrowDownwardIcon /></IconButton >
          <IconButton disabled={!props.editable} className='orderingButton' color='secondary' size="small" onClick={(e) => props.deleteInput(props.input.iid)}><DeleteIcon /></IconButton >
        </ButtonGroup>
      </TableCell>
    </TableRow>
  ) // return
} // PageSelector() render fn


